<template>
  <div>
    <v-dialog v-model="duplicateCredentialDialog" max-width="500">
      <v-card>
        <v-card-title class="md-subheader-no-spacing"
          >Duplicate Shopper Info</v-card-title
            >
            <v-card-text>
              <div class="mb-1 md-body-no-spacing">
                Only one store can be active when sharing shopper login
                credentials or custom URL.
              </div>
              <div
                v-if="!createdByAnotherSalesRep"
                class="mt-3 md-body-no-spacing"
              >
                The active store is:
                <strong>
                  {{ activatedCampaign ? activatedCampaign.name : "" }}
                </strong>
              </div>
              <div v-else class="mt-2 md-body-no-spacing">
                Another member of your team has an active store with the same
                shopper credentials. Contact your distributor manager, or
                change your credentials
              </div>
              <div
                v-if="disableDeactiveCampaignBy"
                class="mt-3 md-body-no-spacing"
              >
                Your {{ disableDeactiveCampaignBy }} currently has a store
                that's activated with the same credentials. Enter different
                credentials to activate this store or contact your
                {{ disableDeactiveCampaignBy }} to discuss changes
              </div>
            </v-card-text>

            <v-divider></v-divider>

        <v-card-actions class="py-3 px-3">
          <v-btn
            variant="flat"
            @click="cancelDuplicateCredentialDialog()"
            data-testid="cl-deactivate-campaign"
            >CANCEL</v-btn
          >
          <v-tooltip location="top">
            <template v-slot:activator="{ props }">
              <v-btn
                v-if="
                  !disableDeactiveCampaignBy && !createdByAnotherSalesRep
                "
                variant="flat"
                @click="$emit('deactivate-campaign')"
                data-testid="cl-dup-deactivate-store"
                v-bind="props"
                >DEACTIVATE STORE</v-btn
              >
            </template>
            <span>
              Deactivate original store and activate selected store
            </span>
          </v-tooltip>
          <v-spacer />
          <v-tooltip location="top">
            <template v-slot:activator="{ props }">
              <v-btn
                variant="flat"
                class="ml-2"
                color="primary"
                @click="$emit('open-credentials')"
                data-testid="cl-change-credentials"
                v-bind="props"
              >
                CHANGE INFO
              </v-btn>
            </template>
            <span> Change login credentials or URL of selected store </span>
          </v-tooltip>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="changeCredentialDialog" max-width="500">
      <v-card>
        <v-card-title class="md-subheader-no-spacing"
          >Change Shopper Info</v-card-title
        >
        <v-card-text>
          <div class="md-body-no-spacing">
            <strong class="mb-3">
              {{ selectedStore ? selectedStore.name : "" }}
            </strong>

            <v-form v-model="localValidForm">
              <div v-if="displayChangeCredentialField">
                <div class="md-light">
                  An active store is currently using this account. To
                  activate your store, change your shopper credentials here.
                </div>
                <v-text-field
                  v-model="localShopperLogin"
                  label="Login *"
                  :rules="[
                    validateUsername,
                    validateNotEmpty,
                    validateUserExist(userExist),
                  ]"
                  @update:modelValue="inputUsername"
                  data-testid="ct-shopper-login"
                />

                <v-text-field
                  autocomplete="new-password"
                  :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                  :rules="[validateNotEmpty]"
                  label="Password *"
                  v-model="localShopperPassword"
                  class="input-group--focused"
                  @update:modelValue="changePassword()"
                  @keydown="inputPassword"
                  @click:append="showPassword = !showPassword"
                  data-testid="ct-shopper-password"
                />
              </div>
              <v-form v-model="localCustomUrlValid" v-if="displayChangeURLField">
                <div class="md-light">
                  An active store is currently using this URL. To activate
                  your store, change your URL here.
                </div>
                <div class="d-flex align-center">
                  <v-tooltip location="start">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        icon variant="plain"
                        @click="copyUrlToClipboard"
                        v-bind="props"
                        :disabled="!localShopperCustomLink || !localCustomUrlValid"
                      >
                        <v-icon icon="content_copy" />
                      </v-btn>
                    </template>
                    <span> Copy Link </span>
                  </v-tooltip>
                  <v-text-field
                    v-model="localShopperCustomLink"
                    :rules="[
                      ...customLinkRules,
                      validateShopperURLExist(shopperURLExist),
                    ]"
                    :prefix="baseUrl + '/shop/'"
                    label="Custom Shopper Link"
                    hint="Create a custom link to allow shoppers to access the storefront without a login and password"
                    @update:modelValue="inputShopperURL"
                    persistent-hint
                  />
                </div>
              </v-form>
            </v-form>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="py-3 px-3">
          <v-btn
            variant="flat"
            @click="cancelChangeCredentialDialog()"
            data-testid="cl-deactivate-campaign"
            >CANCEL</v-btn
          >
          <v-spacer />
          <v-btn
            variant="flat"
            color="primary"
            @click="$emit('change-credentials', {
              login: localShopperLogin,
              password: passwordInput ? localShopperPassword : null,
              customLink: localShopperCustomLink,
            })"
            data-testid="cl-deactivate-campaign"
            :disabled="confirmDisabled"
            >CONFIRM</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import rules from "@/mixins/rules";

export default {
  mixins: [rules],

  props: {
    duplicateDialogOpen: { type: Boolean, default: false },
    changeDialogOpen: { type: Boolean, default: false },
    activatedCampaign: { type: Object, default: null },
    selectedStore: { type: Object, default: null },
    createdByAnotherSalesRep: { type: Boolean, default: false },
    disableDeactiveCampaignBy: { type: String, default: "" },
    displayChangeCredentialField: { type: Boolean, default: false },
    displayChangeURLField: { type: Boolean, default: false },
    shopperLogin: { type: String, default: "" },
    shopperPassword: { type: String, default: "" },
    shopperCustomLink: { type: String, default: "" },
    activatedCampaignShopperUsername: { type: String, default: "" },
    activatedCampaignShopperURL: { type: String, default: "" },
  },

  emits: [
    'deactivate-campaign',
    'open-credentials',
    'change-credentials',
    'update:duplicateDialogOpen',
    'update:changeDialogOpen',
    'update:validForm',
  ],

  data() {
    return {
      showPassword: true,
      localShopperLogin: this.shopperLogin,
      localShopperPassword: this.shopperPassword,
      localShopperCustomLink: this.shopperCustomLink,
      localValidForm: false,
      localCustomUrlValid: null,
      userExist: false,
      shopperURLExist: false,
      passwordInput: false,
    };
  },

  computed: {
    duplicateCredentialDialog: {
      get() { return this.duplicateDialogOpen; },
      set(val) { this.$emit('update:duplicateDialogOpen', val); },
    },
    changeCredentialDialog: {
      get() { return this.changeDialogOpen; },
      set(val) { this.$emit('update:changeDialogOpen', val); },
    },
    baseUrl() {
      return process.env.BASE_URL || window.location.origin;
    },
    customLinkRules() {
      const rules = [];
      rules.push(
        (v) => !v || (v && !v.includes(" ")) || "Url cannot contain spaces"
      );
      rules.push(
        (v) => !v || (v && !v.includes("/")) || "Url cannot contain backslash"
      );
      return rules;
    },

    // CONFIRM enable rule. The dialog can show three field-presence
    // combos depending on which fields collide; Vuetify 3 does NOT
    // propagate nested-form validity to the outer form, so binding
    // disabled to a single `localValidForm` flag stranded the
    // URL-only path (#1437): outer form has zero validatable fields
    // when displayChangeCredentialField=false, so localValidForm
    // never flips and CONFIRM was permanently disabled.
    confirmDisabled() {
      const wantUrl = this.displayChangeURLField;
      const wantCreds = this.displayChangeCredentialField;
      if (wantUrl && !wantCreds) {
        return !this.localCustomUrlValid || !this.localShopperCustomLink;
      }
      if (wantCreds && !wantUrl) {
        return !this.localValidForm;
      }
      if (wantCreds && wantUrl) {
        return (
          !this.localValidForm ||
          !this.localCustomUrlValid ||
          !this.localShopperCustomLink
        );
      }
      // Neither field shown -- dialog has nothing to submit; keep
      // CONFIRM disabled rather than emitting an empty payload.
      return true;
    },
  },

  watch: {
    shopperLogin(val) { this.localShopperLogin = val; },
    shopperPassword(val) { this.localShopperPassword = val; this.passwordInput = false; },
    shopperCustomLink(val) { this.localShopperCustomLink = val; },
    localValidForm(val) { this.$emit('update:validForm', val); },
  },

  methods: {
    copyUrlToClipboard() {
      navigator.clipboard.writeText(
        this.baseUrl + "/shop/" + this.localShopperCustomLink
      );
    },

    inputUsername() {
      this.userExist = this.activatedCampaignShopperUsername == this.localShopperLogin;
    },

    inputShopperURL() {
      this.shopperURLExist = !!(
        this.localShopperCustomLink &&
        this.activatedCampaignShopperURL == this.localShopperCustomLink
      );
    },

    changePassword() {
      this.passwordInput = true;
    },

    inputPassword(event) {
      if (!this.passwordInput) {
        this.localShopperPassword = "";
        this.passwordInput = true;
      }
    },

    validateUserExist(val) {
      return !val || "Username already exists";
    },

    validateShopperURLExist(val) {
      return !val || "Shopper URL already exists";
    },

    cancelDuplicateCredentialDialog() {
      this.duplicateCredentialDialog = false;
    },

    cancelChangeCredentialDialog() {
      this.changeCredentialDialog = false;
      // Re-open the duplicate dialog if we came from there
      if (!this.$parent?.showCredentialDialogDirectly) {
        this.duplicateCredentialDialog = true;
      }
    },
  },
};
</script>
